// steamdata.dev frontend: a tiny client-side router over the steam-api API.
//
//   /             tracked games, highest current player count first (?page=2...),
//                 page 0 led by cards for the main tags
//   /apps/:id     one game: latest count, all-time peak, range high/low,
//                 chart (?range=24h|7d|...)
//   /search       games by name and tag (?q=&trend=rising&busy_hours=europe),
//                 as rows like the list's
//   /tags         every tag page, by tag (a static page, tags.html, written
//                 by build.ts)
//   /tags/:tag/:value
//                 the ranked games with a tag, as rows like the list's
//                 (?page=2...)
//   /developer/:slug, /publisher/:slug
//                 the games a company developed or published, as rows like
//                 the list's (?page=2...)
//   /compare      up to four games' counts on one chart (?apps=730,570&range=30d;
//                 compare.ts, imported only there)
//   /about        how the counts are taken and what the figures mean
//                 (about.ts; a static page, about.html, written by build.ts)
//   /terms, /privacy
//                 the Terms of use and Privacy policy (legal.ts; static pages,
//                 terms.html and privacy.html, written by build.ts)
//
// This is the router and startup: it picks the page for each URL and
// imports the pages most visitors never open. The pages and what's around
// them are drawn by modules of their own (import each name from the module
// that owns it):
//
//   router.ts          following links and back/forward; where back links go
//   render-game.ts     a game's page: its toggles, range tabs and stats
//   chart-draw.ts      the game chart, its tooltip and update menu
//   chart-overlays.ts  where updates, sales and evenings fall on the chart
//   toggles.ts         what a game page shows, as its reader toggled it
//   prefs.ts           what's remembered per browser (localStorage)
//   render-lists.ts    the ranking, tags, tag, company, search, not-found pages
//   local-pages.ts     the formats pages are written in: the reader's own
//   ui.ts              the theme toggle, header search box, art, embed Copy
//   head.ts            a page's <head> tags, and its title said on arrival
//   state.ts           the view now shown, and the shared API reader
//   api-client.ts      the API reader's cache, and what a failure says
//   dom.ts             the #app element, and finding the page's elements
//
// What a game's page and rows say is in game.ts and the modules it puts
// together (see its header), shared with the Worker that
// writes the same page into index.html (../src/index.ts).

import type { CompanyResponse, TagResponse } from "../../shared/api.ts";
import { TAGS_PATH } from "../../shared/site.ts";
import { LOAD_ERROR } from "./api-client.ts";
import { app } from "./dom.ts";
// Sets the formats every page is written in, before any is drawn.
import "./local-pages.ts";
import { setMeta } from "./head.ts";
import { companyMeta, tagMeta } from "./meta.ts";
import { renderApp } from "./pages/render-game.ts";
import { renderList, renderNotFound, renderPagedPage, renderSearch, renderTags } from "./pages/render-lists.ts";
import { backLink, onRoute } from "./router.ts";
import { APP_PATH, companyApiPath, companyRoute, COMPARE_PATH, SEARCH_PATH, tagApiPath, tagPageOf } from "./routes.ts";
import { isCurrent, newView, prerendered } from "./state.ts";
import { showSearchBox } from "./ui.ts";
import { companyPage, tagPage } from "./pages/list-pages.ts";

function route(): void {
  const view = newView();

  const appId = location.pathname.match(APP_PATH)?.[1];
  const tagged = tagPageOf(location.pathname);
  const company = companyRoute(location.pathname)?.page;

  // The search page has its own box, so the header's is hidden there.
  showSearchBox(location.pathname !== SEARCH_PATH);

  if (location.pathname === "/") {
    renderList(view);
  } else if (location.pathname === SEARCH_PATH) {
    renderSearch(view);
  } else if (location.pathname === COMPARE_PATH) {
    // Imported only here, as the About page is; it draws itself.
    lazyPage(view, () => import("./pages/compare.ts"), (compare) => compare.renderCompare(view));
  } else if (location.pathname === "/about") {
    // Most visitors never open it. The static about.html arrives with it
    // already drawn.
    lazyPage(view, () => import("./pages/about.ts"), ({ aboutMeta, aboutPage }) => {
      setMeta(aboutMeta);
      if (!prerendered(view)) app.innerHTML = aboutPage(backLink());
    });
  } else if (location.pathname === "/terms" || location.pathname === "/privacy") {
    // The static terms.html and privacy.html arrive with them already drawn.
    const terms = location.pathname === "/terms";
    lazyPage(view, () => import("./pages/legal.ts"), (legal) => {
      setMeta(terms ? legal.termsMeta : legal.privacyMeta);
      if (!prerendered(view)) app.innerHTML = (terms ? legal.termsPage : legal.privacyPage)(backLink());
    });
  } else if (location.pathname === TAGS_PATH) {
    renderTags(view);
  } else if (tagged) {
    renderPagedPage<TagResponse>(view, {
      apiPath: (page) => tagApiPath(tagged.tag, tagged.value, page),
      meta: (page, list) => tagMeta(tagged, page, list),
      body: (page, list) => tagPage(tagged, page, list),
      label: () => tagged.def.title
    });
  } else if (company) {
    renderPagedPage<CompanyResponse>(view, {
      apiPath: (page) => companyApiPath(company.role, company.slug, page),
      meta: (page, list) => companyMeta(company, page, list),
      body: (page, list) => companyPage(company, page, list),
      label: (list) => list.name
    });
  } else if (appId) {
    renderApp(view, Number(appId));
  } else {
    renderNotFound();
  }
}

// A page whose module is imported when it opens (`load`), drawn by `draw`
// while view `token` is still the one shown. If the module won't load, or
// drawing fails, the page says so. Exported for its tests, which can't make
// a real import fail.
export async function lazyPage<T>(token: number, load: () => Promise<T>, draw: (page: T) => unknown): Promise<void> {
  try {
    const page = await loadPage(load);
    if (!isCurrent(token)) return;
    await draw(page);
  } catch {
    if (isCurrent(token)) app.innerHTML = `${backLink()}<div class="panel status">${LOAD_ERROR}</div>`;
  }
}

// A page's module, imported when the page opens. Each is a chunk named by a
// hash of its content (build.ts), and a deploy replaces them all, so a tab
// opened before one asks for a chunk that's gone: load the page afresh, for
// the new build. Only then: a chunk that won't load for any other reason (the
// network, offline) shows the page's error, and the new build's own page
// never reloads again.
async function loadPage<T>(load: () => Promise<T>): Promise<T> {
  try {
    return await load();
  } catch (err) {
    if (!(await deployedSince())) throw err;
    location.reload();
    // Nothing to draw: the page is going.
    return new Promise<T>(() => {});
  }
}

// Whether the site has been deployed since this page loaded: its pages no
// longer run this script. Asked of /about, a static page (about.html) that
// never runs the Worker; false if it can't be asked.
async function deployedSince(): Promise<boolean> {
  try {
    const response = await fetch("/about", { cache: "no-store" });
    return response.ok && !(await response.text()).includes(new URL(import.meta.url).pathname);
  } catch {
    return false;
  }
}

onRoute(route);
route();
